// Send a training reference document to a customer (Session AFP, Stream B). Opened from the
// Reference Library (an on-demand send) and from a completed training session (offer to send the
// matching customer-facing reference to the customer). The send is a consequential outward action,
// so it is a two-step confirm that NAMES the recipient (the BRANDED CONFIRM rule); the office
// always sees the address before anything is sent.
//
// When a siteId is passed (the session flow), the customer's contact email is pre-filled and shown
// for confirmation; it stays editable, and the office can send to any address.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import type { TrainingReferenceDocumentDto } from "../../lib/trainingTypes";
import { Button } from "../../components/Button";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

// A light client-side email check, enough to arm the confirm; the server validates authoritatively.
function looksLikeEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function SendTrainingDocModal({
  doc,
  siteId,
  onClose,
  onSent,
}: {
  doc: TrainingReferenceDocumentDto;
  siteId?: string;
  onClose: () => void;
  onSent: (message: string) => void;
}) {
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill the recipient from the site's customer contact (the session flow). Never blocks: a
  // failed lookup just leaves the fields empty for the office to type.
  useEffect(() => {
    if (!siteId) return;
    let live = true;
    api.trainingDocuments
      .siteContact(siteId)
      .then((c) => {
        if (!live) return;
        if (c.email) setTo(c.email);
        if (c.name) setName(c.name);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [siteId]);

  // Editing the recipient disarms, so the second press can only ever send to the address on screen.
  function editTo(v: string) {
    setTo(v);
    setArmed(false);
    setError(null);
  }

  async function onPrimary() {
    if (!looksLikeEmail(to)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!armed) {
      setArmed(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.trainingDocuments.send(doc.id, { to: to.trim(), recipientName: name.trim() || null, siteId });
      onSent(`"${doc.title}" sent to ${to.trim()}.`);
    } catch (err) {
      setError(friendlyApiError(err, "Could not send the document. Please try again."));
      setArmed(false);
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Send training reference document" onClose={() => !busy && onClose()} width="max-w-lg" panelClassName="p-6">
      <h2 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Send reference document</h2>
      <p className="mb-4 text-sm text-ele-body">
        {doc.trainingTypeLabel}: <span className="text-ele-strong">{doc.title}</span>
        {doc.version ? ` (${doc.version})` : ""}. This attaches the PDF to a branded email.
      </p>
      <div className="grid gap-3">
        <label>
          <span className={labelCls}>Recipient email</span>
          <input value={to} onChange={(e) => editTo(e.target.value)} placeholder="name@example.com" className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Recipient name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="For the greeting" className={inputCls} />
        </label>
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        {/* The two-step send: the first press arms (the button turns the solid red CTA and names the
            recipient), the second sends. */}
        <Button variant={armed ? "danger" : "primary"} onClick={() => void onPrimary()} disabled={busy} className={MODAL_ACTION_BTN}>
          {busy ? "Sending..." : armed ? `Press again to send to ${to.trim()}` : "Send to customer"}
        </Button>
      </div>
    </ModalShell>
  );
}
